import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { LazyPreviewService } from 'src/app/services/lazy-preview.service';
import { DocumentService } from 'src/app/modules/document/document.service';
import { ImagePreviewComponent } from 'src/app/modules/shared/image-preview/image-preview.component';
import { DocumentImageAnnotatorComponent } from 'src/app/modules/document/document-image-annotator/document-image-annotator.component';
import { VideoPreviewComponent } from 'src/app/modules/shared/video-preview/video-preview.component';
import { AudioPreviewComponent } from 'src/app/modules/shared/audio-preview/audio-preview.component';

/**
 * The application's in-app document preview.
 *
 * This is the dispatcher the Document Manager's list has always used, lifted
 * into a service so any screen can open the *same* preview instead of falling
 * back to `window.open`. `DocumentListPartialComponent.openImagePreview()` and
 * the Bid Room both delegate here, so there is one implementation and the two
 * cannot drift apart.
 *
 * Routes by file type:
 *   image → ImagePreviewComponent · video → VideoPreviewComponent
 *   audio → AudioPreviewComponent · pdf → PDF editor dialog (lazy)
 *   csv/xls/xlsx → spreadsheet editor (lazy)
 *   txt/log/json/xml/md/doc/docx → text/word editor (lazy)
 *   anything else → basic preview with a download option
 */
@Injectable({ providedIn: 'root' })
export class DocumentPreviewService {
  private readonly dialog = inject(MatDialog);
  private readonly lazyPreview = inject(LazyPreviewService);
  private readonly documentService = inject(DocumentService);

  /**
   * @param model a raw document row (needs at least `file_url` and `file_type`)
   */
  open(model: any): void {
    if (!model) { return; }

    const fileType = String(model?.file_type || '').toLowerCase();
    const { file_url, ...rest } = model;
    const modifiedModel = { ...rest, url: file_url };

    if (this.documentService.allowedImageTypes().includes(fileType)) {
      this.dialog.open(DocumentImageAnnotatorComponent, {
        width: '92vw',
        maxWidth: '92vw',
        height: '88vh',
        data: modifiedModel,
        panelClass: 'document-image-annotator-panel'
      });
      return;
    }

    if (this.documentService.allowedVideoTypes()?.includes(fileType)) {
      this.dialog.open(VideoPreviewComponent, {
        width: '80%',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
      return;
    }

    if (this.documentService.allowedAudioTypes()?.includes(fileType)) {
      this.dialog.open(AudioPreviewComponent, {
        width: '600px',
        maxWidth: '90vw',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
      return;
    }

    if (fileType === 'pdf') {
      this.lazyPreview.openPdfPreview({
        width: '90vw',
        height: '90vh',
        maxWidth: '1400px',
        maxHeight: '900px',
        data: {
          id: model?.id,
          file_url: model?.file_url,
          original_name: model?.original_name,
          file_type: model?.file_type,
          file_size: model?.file_size,
          creator: model?.creator,
          uploaded_by: model?.uploaded_by,
          created_by: model?.created_by,
          module: model?.module,
          linked_module: model?.linked_module,
          linked_record: model?.linked_record
        },
        panelClass: 'pdf-preview-dialog-container',
        disableClose: false,
        hasBackdrop: true,
        autoFocus: false
      });
      return;
    }

    if (fileType === 'csv' || fileType === 'xlsx' || fileType === 'xls') {
      this.lazyPreview.openCsvPreview({
        width: '95vw',
        height: '95vh',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
      return;
    }

    if (['txt', 'log', 'json', 'xml', 'md', 'doc', 'docx'].includes(fileType)) {
      this.lazyPreview.openTextPreview({
        width: '95vw',
        height: '95vh',
        maxWidth: '95vw',
        maxHeight: '95vh',
        data: modifiedModel,
        panelClass: 'media-preview-dialog'
      });
      return;
    }

    // Unsupported: basic preview with a download option.
    this.dialog.open(ImagePreviewComponent, {
      width: '600px',
      data: modifiedModel,
      panelClass: 'media-preview-dialog'
    });
  }
}
